// ============================================================
// adminUi.tsx — primitives for the Admin redesign (mockup
// casevo-admin-redesign.html, Option A). Builds on the Settings
// primitives (settingsUi.tsx: SettingsCard, buttons, SIcon, switch,
// badge, saved) so Admin and Settings are visual siblings. Every value is
// copied from the mockup via Tailwind arbitrary syntax; token colours →
// light + dark, with explicit dark: variants where the mockup overrides.
// ============================================================
import React from 'react'
import { SIcon } from '../settings/settingsUi'
import type { SettingsIconName } from '../settings/settingsUi'

// .sel — native <select> styled 36px, radius 9, input bg.
export const A_SELECT =
  'h-[36px] border border-casevo-border rounded-[9px] bg-casevo-input px-[10px] text-[13.5px] text-casevo-forest ' +
  'focus:outline-none focus:border-casevo-mid casevo-focus disabled:opacity-60 disabled:cursor-not-allowed'

// .chip / .chip.on — rounded filter chips (Team status filter, Permissions
// Firm defaults | By member switch).
export const A_CHIP_BASE =
  'inline-flex items-center gap-[4px] text-[13px] px-[12px] py-[6px] rounded-full border transition-colors focus:outline-none casevo-focus'
export const A_CHIP_ON = 'border-casevo-brand bg-casevo-mint text-casevo-forest dark:text-[#cfeedd] font-semibold'
export const A_CHIP_OFF = 'border-casevo-border bg-casevo-surface text-casevo-brand dark:text-casevo-mid hover:border-casevo-mid'
// Count text: #5c6a61 in light (mockup --soft was 4.1:1 on the active mint
// chip); dark keeps the token (4.75:1).
export const A_CHIP_COUNT = 'text-[11.5px] text-[#5c6a61] dark:text-casevo-text-soft font-medium ml-[4px]'

// .tbl — simple admin table.
export const A_TH = 'text-[11px] font-bold tracking-[0.12em] uppercase text-casevo-text-soft text-left px-[12px] py-[10px] border-b border-casevo-border-soft'
export const A_TD = 'px-[12px] py-[12px] border-b border-casevo-border-soft align-middle'

// .ov — "Overridden" / override-count tag (amber, not gold: it flags a
// deviation from the firm default).
export const OverrideTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center text-[10.5px] font-bold tracking-[0.08em] uppercase rounded-[5px] px-[6px] py-px border bg-[#fff7e6] border-[#f1d9a6] text-[#7a5a12] dark:bg-[#3a3220] dark:border-[#5c4c26] dark:text-[#f1d9a6]">
    {children}
  </span>
)

// .pgrp — permission AREA heading (Tasks / Cases / Appointments).
export const PermGroupHeading: React.FC<{ icon: SettingsIconName; label: string; count?: string }> = ({ icon, label, count }) => (
  <div className="flex items-center gap-[9px] pt-[18px] pb-[4px] text-[12px] font-bold tracking-[0.16em] uppercase text-casevo-brand dark:text-[#7fd1a8]">
    <SIcon name={icon} size={14} />
    <span>{label}</span>
    {count && <span className="text-[11px] tracking-[0.06em] font-semibold text-casevo-text-soft">{count}</span>}
    <span className="flex-1 h-px bg-casevo-border-soft" aria-hidden="true" />
  </div>
)

// .prow — one permission row (divider between rows, none under a heading).
export const PERM_ROW = 'grid grid-cols-[minmax(0,1fr)_auto] gap-[16px] items-center py-[12px] border-t border-casevo-border-soft'
export const PERM_ROW_FIRST = 'grid grid-cols-[minmax(0,1fr)_auto] gap-[16px] items-center py-[12px]'

// .pico — small mint icon tile before a permission title.
export const PermIcon: React.FC<{ icon: SettingsIconName }> = ({ icon }) => (
  <span className="w-[26px] h-[26px] rounded-[8px] bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] grid place-items-center shrink-0" aria-hidden="true">
    <SIcon name={icon} size={14} />
  </span>
)

// .tri — Inherit (On/Off) / Granted / Denied segmented control.
export type TriState = 'inherit' | 'granted' | 'denied'
export const TristateControl: React.FC<{
  value: TriState
  firmDefault: boolean
  onChange: (next: TriState) => void
  disabled?: boolean
  label: string
}> = ({ value, firmDefault, onChange, disabled, label }) => {
  const seg = 'h-full flex items-center gap-[5px] px-[11px] text-[12.5px] whitespace-nowrap transition-colors focus:outline-none casevo-focus disabled:cursor-not-allowed'
  const idle = 'text-casevo-brand dark:text-casevo-mid hover:bg-casevo-mint/40'
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`inline-flex h-[32px] border border-casevo-border rounded-[9px] overflow-hidden bg-casevo-surface shrink-0 ${disabled ? 'opacity-60' : ''}`}
    >
      <button
        type="button" role="radio" aria-checked={value === 'inherit'} disabled={disabled}
        onClick={() => onChange('inherit')}
        className={`${seg} ${value === 'inherit' ? 'font-semibold bg-[#e8f1ec] dark:bg-[#31443a] text-casevo-forest' : idle}`}
      >
        Inherit <em className="not-italic text-[11px] font-medium text-[#5c6a61] dark:text-[#a3bdb0]">({firmDefault ? 'On' : 'Off'})</em>
      </button>
      <button
        type="button" role="radio" aria-checked={value === 'granted'} disabled={disabled}
        onClick={() => onChange('granted')}
        className={`${seg} border-l border-casevo-border ${value === 'granted' ? 'font-semibold bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8]' : idle}`}
      >
        Granted
      </button>
      <button
        type="button" role="radio" aria-checked={value === 'denied'} disabled={disabled}
        onClick={() => onChange('denied')}
        className={`${seg} border-l border-casevo-border ${value === 'denied' ? 'font-semibold bg-[#fdecec] text-[#b42318] dark:bg-[rgba(127,29,29,0.35)] dark:text-[#fca5a5]' : idle}`}
      >
        Denied
      </button>
    </div>
  )
}

// Radio-option card list (Case history / Task notifications). Buttons with
// role=radio so each option is focusable with the casevo-focus ring.
export const RadioOptionList = <T extends string>({
  options, value, onChange, label, disabled,
}: {
  options: { value: T; label: string; help: string }[]
  value: T
  onChange: (v: T) => void
  label: string
  disabled?: boolean
}): React.ReactElement => (
  <div role="radiogroup" aria-label={label} className="flex flex-col gap-[10px]">
    {options.map((o) => {
      const on = o.value === value
      return (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={on}
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={`flex gap-[12px] items-start text-left px-[14px] py-[12px] border rounded-[12px] transition-colors focus:outline-none casevo-focus disabled:opacity-60 ${
            on ? 'border-casevo-brand bg-casevo-mint' : 'border-casevo-border bg-casevo-surface hover:border-casevo-mid'
          }`}
        >
          <span className={`w-[18px] h-[18px] rounded-full border-2 mt-px grid place-items-center shrink-0 ${on ? 'border-casevo-primary dark:border-[#74c69d]' : 'border-casevo-border'}`} aria-hidden="true">
            {on && <span className="w-[8px] h-[8px] rounded-full bg-casevo-primary dark:bg-[#74c69d]" />}
          </span>
          <span className="min-w-0">
            <span className="block text-[14px] font-medium text-casevo-forest">{o.label}</span>
            <span className="block text-[12.5px] text-casevo-text-soft mt-[3px] leading-[1.45]">{o.help}</span>
          </span>
        </button>
      )
    })}
  </div>
)

// Gold "You" tag — the design language's restrained gold accent (§3.2).
export const YouTag: React.FC = () => (
  <span className="text-[10.5px] font-bold px-[7px] py-px rounded-full bg-casevo-gold text-[#3a2e08] ml-[6px] align-[1px]">You</span>
)
